import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';

export interface RawTradingData {
    trader: string;
    pairIndex: number;
    index: number;
    positionSize: string;
    openFeeP: string;
    buy: boolean;
    leverage: string;
    tp: string;
    sl: string;
    openPrice: string;
    minPrice: string;
    maxPrice: string;
    type: string;
    isLimitOrder: boolean;
    executionPrice: string;
    initialPosToken: string;
    collateralBeforeFee: string;
    createdAt: string; // ISO date string
    aSpreadP: string;
}

export interface RawPairInfo {
    pairIndex: string;
    symbol: string;
    openInterestLong: string;
    openInterestShort: string;
    maxOpenInterest: string;
    fundingRateLong: string;
    fundingRateShort: string;
    rolloverRate: string;
    marketCap: string | null;
    fdv: string | null;
    maxSupply: string | null;
    maxLeverage: string;
    spread: string;
    onePercentDepthAbove: string;
    onePercentDepthBelow: string;
}

export interface RawPendingData {
    trade: {
        trader: string;
        pairIndex: string;
        index: string;
        initialPosToken: string;
        positionSizeUsdc: string;
        openPrice: string;
        buy: boolean;
        leverage: string;
        tp: string;
        sl: string;
    };
    block: string;
    wantedPrice: string;
    slippageP: string;
    spreadReductionP: string;
    tokenId: string;
    pendingId: string;
}

export interface TradingData {
    trade_info: {
        trader: string;
        pair_index: number;
        index: number;
        initial_pos_token: number;
        position_size_usd: number;
        open_price: number;
        buy: boolean;
        leverage: number;
        tp: number;
        sl: number;
    };
    open_fee: number;
    min_price: number;
    max_price: number;
    block: number;
    type: number;
    spread: number;
    execution_price: number;
    collateral_before_fee: number;
    created_at: {
        seconds: number;
        nanos: number;
    };
    lastUpdatedDate: number;
}

export interface BookingState {
    pair_index: number;
    symbol: string;
    open_interest_long: string; // thường là BigNumber hoặc string do giá trị lớn
    open_interest_short: string;
    max_open_interest: string;
    funding_rate_long: string; // tỷ lệ thường để string hoặc number nếu đã convert
    funding_rate_short: string;
    rollover_rate: string;
    max_leverage: number;
    spread: string;
    one_percent_depth_above: string;
    one_percent_depth_below: string;
    open_fee: string;
    nft_limit_order_fee: string;
}

export interface OrderTradeType {
    openTrade: TradingData[];
    pendingTrade: TradingData[];
}

export interface PendingDataProps {
    trade_info: {
        buy: boolean;
        trader: string;
        pair_index: number;
        index: number;
        initial_pos_token: number;
        position_size_usd: number;
        open_price: number;
        leverage: number;
        tp: number;
        sl: number;
    };
    block: number;
    wanted_price: number;
    slippage: number;
    spread_reduction: number;
    token_id: number;
    pending_id: number;
}

interface AppState {
    orderTrade: OrderTradeType;
    booking: BookingState[];
    pendingOrder: PendingDataProps[];
    count: {
        openTrade: number;
        pendingTrade: number;
        pendingOrder: number;
    };
    removeOrderByIndex: (index: number) => void;
    setPendingOrder: (dataSocket: PendingDataProps[]) => void;
    setOrderTradeData: (dataSocket: TradingData[]) => void;
    setBookingData: (dataSocket: BookingState[]) => void;
    resetOrderTradeData: () => void;
    // getOrderTradeData: (
    //   account: string,
    //   contractAddress: string
    // ) => Promise<void>;
}

export const useTradeStore = createWithEqualityFn<
    AppState,
    [['zustand/immer', never]]
>(
    immer((set, get) => ({
        modalDownLoad: false,
        count: {
            openTrade: 0,
            pendingTrade: 0,
            pendingOrder: 0,
        },
        orderTrade: {
            openTrade: [],
            pendingTrade: [],
        },
        pendingOrder: [],

        setPendingOrder: (dataSocket: PendingDataProps[]) => {
            set((state) => {
                state.pendingOrder = dataSocket;
                state.count.pendingOrder = dataSocket.length;
            });
        },
        booking: [],
        setOrderTradeData: (dataSocket: TradingData[]) => {
            const openTrade =
                dataSocket?.filter((x) => Number(x.type) === 0) || [];
            const pendingTrade = dataSocket?.filter((x) => x.type === 1) || [];

            const orderTrade = {
                openTrade,
                pendingTrade,
            };

            set((state) => {
                state.orderTrade = orderTrade;
                state.count.openTrade = orderTrade.openTrade.length;
                state.count.pendingTrade = orderTrade.pendingTrade.length;
            });
        },
        resetOrderTradeData: () => {
            set((state) => {
                state.orderTrade.openTrade = [];
                state.orderTrade.pendingTrade = [];
                state.booking = [];
                state.count = {
                    openTrade: 0,
                    pendingTrade: 0,
                    pendingOrder: 0,
                };
            });
        },
        setBookingData: (dataSocket: BookingState[]) => {
            set((state) => {
                state.booking = dataSocket;
            });
        },
        removeOrderByIndex: (index: number) => {
            const openTrade = get().orderTrade.openTrade;
            const temp = JSON.parse(JSON.stringify(openTrade));
            temp.splice(index, 1);
            set((state) => {
                state.orderTrade.openTrade = [...temp];
                state.count.openTrade = [...temp].length;
            });
        },
    }))
);
